import { Log, User, WebStorageStateStore } from "oidc-client-ts";
import { AuthProvider, AuthProviderProps } from "react-oidc-context";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { config } from "../utils/config";
import { useGlobal } from "./GlobalProviderLayout";
import { CLOSE_TOAST, IToast, OPEN_TOAST } from "./GlobalProviderLayout/GlobalProviderLayoutModel";

/**
 * AuthProviderLayout is a Layout Route (see react-router-dom) and is responsible for configuring and providing 
 * an Auth Context for all descendents. This for example, enables descendent components to "useAuth()" for user info
 * @returns 
 */
const AuthProviderLayout = () => {
  if (config.oidcClientTsDebugLog) {
    // enable debug logging
    Log.setLogger(console);
    Log.setLevel(Log.DEBUG);
  }
  const global = useGlobal();
  const navigate = useNavigate();

  const authProviderConfig: AuthProviderProps = {
    authority: config.oidcAuthority,
    client_id: config.oidcClientId,
    redirect_uri: window.location.origin + '/signin-callback.html',
    silent_redirect_uri: window.location.origin + '/silent-renew.html',
    post_logout_redirect_uri: window.location.origin,
    response_type: config.oidcResponseType,
    scope: config.oidcScope,
    /**
     * When true (Default is false), monitorSession instructs oidc-client-ts UserManager to monitor session with OP
     * and this allows us to subscribe to events such as UserManager.addUserSignedOut. This is useful to implement
     * features that include "Sign-out in one tab, signs out of all tabs"
     */
    monitorSession: true,
    /**
     * Remove code and state params from the URL, then navigate to route that was active prior to sign-in redirect
     * @param user 
     */
    onSigninCallback: (user: User | void) => {
      const queryParams = new URLSearchParams(window.location.search);
      const isFirstLogin = queryParams.get("id_token") === "isFirstLogin";
      window.history.replaceState({}, document.title, '/');

      if (user && user.state) {
        if (isFirstLogin) {
          const payload: IToast = {
            severity: "info",
            title: "First login successful!",
            msg: <>
              Use this <Link onClick={() => { global.dispatch({ type: CLOSE_TOAST }) }} to="/my-profile">
                link
              </Link> to view your profile details. You can update your email while you're there.
            </>,
            closeTime: 0,
            anchorOrigin: { vertical: "top", horizontal: "center" },
          };
          global.dispatch({ type: OPEN_TOAST, payload });
        }

        navigate(user.state);
      };
    },
    /** 
     * Using LocalStorage (Default is SessionStorage) provides a convenient way for new browser tabs to be automatically 
     * signed in if another tab was previously signed in.
     */
    userStore: new WebStorageStateStore({
      store: localStorage
    }),
    /** Load userInfo from the userinfo endpoint at OP */
    loadUserInfo: false,
    /**
     * When true (Default is true), automaticSilentRenew instructs oidc-client-ts UserManager to a renew the access
     * token when it determines it's current access token is close to expiry (approx 60 seconds). UserManager will
     * renew the token in one of two ways, either: 1. POST /token { grant_type: "refresh_token", refresh_token: ... }
     * or 2. Open up an iFrame, reauthenticate with IdentityServer, and process the result with signinCallback.
     * UserManager will use method #1 (refresh_token) if one was provided in a previous token/ request, otherwise it
     * will use the iFrame method
     */
    automaticSilentRenew: false
  };

  return <AuthProvider {...authProviderConfig}>
    <Outlet />
  </AuthProvider>
}

export default AuthProviderLayout;